import { ReactNode } from 'react';
import {
  Layout, Database, Brain, Search, Shield, Zap, Palette, Server,
  FileSpreadsheet, Lock, Globe, Key, Terminal, Code, Cpu, Activity
} from 'lucide-react';

// --- Icon Mappings (Helper for UI) ---
export const getCourseIcon = (id: CourseId) => {
  switch (id) {
    case 'digital-foundations': return Globe;
    case 'foundation': return FileSpreadsheet;
    case 'production': return Database;
    case 'ai-operator': return Brain;
    case 'data-detective': return Search;
    case 'cyber-security': return Shield;
    case 'automation': return Zap;
    case 'ui-ux': return Palette;
    case 'sovereign-systems': return Server;
    case 'game-dev': return Activity;
    case 'logic-architect': return Code;
    default: return Terminal;
  }
};

export type CourseId =
  | 'digital-foundations' // Course 0 - NEW
  | 'foundation' // Course 1
  | 'production' // Course 2
  | 'ai-operator' // Course 3
  | 'data-detective' // Course 4
  | 'cyber-security' // Course 5
  | 'automation' // Course 6
  | 'ui-ux'      // Course 7
  | 'sovereign-systems' // Course 8
  | 'game-dev' // Course 9
  | 'logic-architect'; // Course 10 - NEW

export type ModuleId =
  // --- Course 1: Foundation (Apps Script) ---
  | 'architecture' | 'variables-types' | 'functions-scope' | 'control-flow'
  | 'spreadsheet-service' | 'loops-arrays' | 'objects-json' | 'formulas'
  | 'external-apis' | 'inventory-logic' | 'triggers-automation' | 'error-handling' | 'deployment-strategies'
  | 'native-features' // Legacy compatibility

  // --- Course 2: Production (Supabase) ---
  | 'relational-design' | 'crud-operations' | 'db-migrations' | 'supabase-setup' | 'data-types-constraints' | 'foreign-keys-joins'
  | 'auth' | 'rls-policies' | 'secure-api' | 'storage'
  | 'edge-functions' | 'db-webhooks' | 'shopping-cart-db' | 'realtime-subs' | 'production-deployment'
  | 'frontend' | 'deployment' | 'capstone-2' // Legacy

  // --- Course 3: AI Operator ---
  | 'llm-fundamentals' | 'prompt-engineering' | 'model-parameters' | 'chain-of-thought'
  | 'few-shot' | 'multimodal' | 'embeddings-vectors' | 'rag-basics'
  | 'function-calling' | 'ai-agents' | 'evals-testing' | 'ai-ethics'

  // --- Course 4: Data Detective (SQL) ---
  | 'sql-select' | 'sorting-limiting' | 'data-cleaning' | 'sql-aggregates' | 'filter-sort'
  | 'grouping-data' | 'sql-joins' | 'date-time-logic' | 'subqueries'
  | 'ctes' | 'window-functions' | 'views-materialized' | 'dashboarding'
  | 'data-detective-sql'

  // --- Course 5: Cyber Guardian ---
  | 'cia-triad' | 'social-engineering' | 'password-strength' | 'cryptography-basics' | 'auth-attacks'
  | 'broken-access-control' | 'sql-injection' | 'xss' | 'csrf'
  | 'secure-headers' | 'network-sniffing' | 'api-security' | 'incident-response'
  | 'ai-phishing' | 'defense-in-depth' | 'ai-encryption' | 'security-usability' | 'phishing' | 'password-cracking' // Legacy

  // --- Course 6: Automation Architect ---
  | 'event-driven-arch' | 'polling-vs-webhooks' | 'cron-scheduling' | 'cron-scheduler' | 'data-transformation'
  | 'conditional-logic' | 'iterators-loops' | 'json-parsing' | 'api-connectors'
  | 'oauth-auth' | 'error-handling-retries' | 'rate-limits' | 'custom-connectors'
  | 'triggers' | 'webhooks'  // Legacy

  // --- Course 7: UI/UX Designer ---
  | 'design-thinking' | 'wireframing' | 'box-model' | 'typography' | 'typography-basics'
  | 'color-theory' | 'flexbox' | 'css-grid' | 'responsive-design'
  | 'components' | 'states-interactivity' | 'animation-motion' | 'accessibility'

  // --- Course 8: Sovereign Systems (Payments & Hosting) ---
  | 'stripe-integration' | 'crypto-payments' | 'docker-containers' | 'vps-setup' | 'server-monitoring'
  | 'coolify-hosting' | 'reverse-proxies' | 'app-hardening' | 'rate-limiting'
  | 'open-source-alternatives' | 'monitoring-uptime' | 'backup-strategies' | 'authentication-identity'

  // --- Course 0: Digital Foundations (NEW) ---
  | 'prerequisites' | 'internet-safety' | 'digital-footprint' | 'research-skills' | 'visual-coding' | 'algorithm-thinking'

  // --- Course 9: Game Development (NEW) ---
  | 'game-loop' | 'sprite-collision' | 'score-system' | 'game-polish' | 'boss-logic'

  // --- Course 10: Logic Architect (Frontend) ---
  | 'react-components' | 'react-state' | 'react-effects' | 'react-props'

  // --- Final Exam Modules (Certification System) ---
  | 'digital-foundations-final-exam' | 'foundation-final-exam' | 'production-final-exam'
  | 'data-detective-final-exam' | 'ai-operator-final-exam' | 'cyber-security-final-exam'
  | 'automation-final-exam' | 'ui-ux-final-exam' | 'sovereign-systems-final-exam'
  | 'game-dev-final-exam' | 'logic-architect-final-exam'

  // Legacy / Common
  | 'home' | 'advanced-db';

export interface Course {
  id: CourseId;
  title: string;
  description: string;
  color: string;
  gradient?: string; // Tailwind gradient classes e.g. "from-green-500 to-emerald-600"
  modules: ModuleConfig[];
  standards?: {
    iste: string; // ISTE-S
    fl: string;
    csta: string;
    ngss?: string; // Next Gen Science
    ccss?: string; // Common Core Math
    stel?: string; // Tech & Engineering
  };
}

export interface ModuleConfig {
  id: ModuleId;
  title: string;
  description: string;
  locked?: boolean;
  type?: 'lesson' | 'quiz'; // 'quiz' triggers the QuizRunner instead of navigation
  path?: string; // Optional URL path override if different from ID
}

export interface Step {
  id: number;
  title: string;
  description: string;
  actionItem?: 'open-sheets' | 'configure-script' | 'copy-code' | 'manual-check' | 'test-url' | 'open-supabase' | 'docker-setup' | 'run-sql' | 'prompt-ai' | 'hack-login';
  buttonText?: string;
  validationCode?: string; // Expected answer or logic
}

export interface ChatMessage {
  role: 'user' | 'model' | 'system';
  text: string;
}

export interface UserProgress {
  xp: number;
  level: number;
  completedModules: ModuleId[];
  badges: string[];
  currentCourse: CourseId | null;
}

export interface ScriptConfig {
  filtering: boolean;
  search: boolean;
  caseSensitive: boolean;
  allowWrite: boolean;
  securityToken: string;
}

export const generateScript = (config: ScriptConfig) => {
  return `/**
 * 🌿 GROUNDED GROWTH - AUTOMATED API
 * Generated by the Automation Architect
 */

function doGet(e) {
  try {
    // 1. Get the data from the first sheet
    var sheet = SpreadsheetApp.getActiveSpreadsheet().getSheets()[0];
    var rows = sheet.getDataRange().getValues();
    
    // 2. Separate Headers (Row 1) and Data (Row 2+)
    var headers = rows[0];
    var data = rows.slice(1);
    
    // 3. Convert Rows to Objects
    var result = data.map(function(row) {
      var obj = {};
      headers.forEach(function(header, i) {
        obj[header] = row[i];
      });
      return obj;
    });

    ${config.filtering ? `
    // [FEATURE] Smart Filtering
    // Example: ?Category=Tech will filter where Column 'Category' is 'Tech'
    if (e && e.parameter) {
      Object.keys(e.parameter).forEach(function(key) {
        if (key === 'search' || key === 'token') return; // Reserved keys
        result = result.filter(function(item) {
          return String(item[key]) == e.parameter[key];
        });
      });
    }` : ''}

    ${config.search ? `
    // [FEATURE] Universal Search
    // Example: ?search=pizza will look for "pizza" in ANY column
    if (e && e.parameter.search) {
       var q = ${config.caseSensitive ? 'e.parameter.search' : 'e.parameter.search.toLowerCase()'};
       result = result.filter(function(item) {
         return Object.values(item).some(function(val) {
           var v = String(val)${config.caseSensitive ? '' : '.toLowerCase()'};
           return v.includes(q);
         });
       });
    }` : ''}

    // 4. Return JSON Response
    return ContentService.createTextOutput(JSON.stringify(result))
      .setMimeType(ContentService.MimeType.JSON);
      
  } catch (error) {
    // Handle Errors gracefully
    return ContentService.createTextOutput(JSON.stringify({ "error": error.toString() }))
      .setMimeType(ContentService.MimeType.JSON);
  }
}

function setup() {
  // Run this once to accepting permissions if needed!
  console.log("Setup complete");
}`;
};